import React, { useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useTheme } from '../../hooks/useTheme';
import { Logo } from '../ui/logo';
import { MoonIcon, SunIcon, ChevronDownIcon, CheckIcon, TabBaseMark, ExternalIcon, InfoIcon, UserIcon } from '../shared/icons';
import SignInDialog from '../SignInDialog';
import { useLessonAccess } from '../../hooks/useLessonAccess';
import { INSTRUMENTS } from '../../lib/instruments';
import { TABBASE_URL } from '../../lib/tabbase-promo';
import { cn } from '../../lib/utils';

/**
 * Header (boards 18 + 20).
 *
 * BREAKPOINT FOR THE WHOLE REBUILD: phone layout below `lg` (1024px), desktop layout at
 * `lg` and up. Steps 4-5 must use the same split (`lg:` prefixes, not `sm:`/`md:`).
 * Both layouts are rendered and swapped with `lg:hidden` / `hidden lg:flex`, so no JS
 * media query is needed.
 *
 * Desktop: instrument text links (real anchors, so open-in-new-tab works), then an
 * "Info" menu and a text dark-mode button. Phone: `Guitar v` chip opening the instrument
 * menu, an icon-only info button and an icon-only dark-mode button.
 *
 * The info menu (both layouts) holds the FAQ and TabBase links, so the phone header stays
 * one row at 360px with the longest instrument name ("Upright Bass"). The FAQ is a static
 * page (public/faq.html, from scripts/generate-faq-page.mjs), so its link is a plain anchor
 * that leaves the app. TabBase opens tabs.scalebase.app in a new tab. Last is Sign in (the
 * private lesson patterns, see src/lib/lesson-access.js), or Sign out once signed in.
 */

const MENU_ITEM_CLASS = cn(
  'min-h-11 cursor-pointer rounded-lg px-3 py-2 gap-3 text-[15px] text-ink',
  'focus:bg-tray focus:text-ink',
);

const INFO_MENU_LABEL = 'Info: FAQ, TabBase and sign in';

/**
 * The info menu: FAQ, TabBase, then Sign in / Sign out. `trigger` is the layout's button
 * look; `onSignIn` opens the password dialog.
 */
function InfoMenu({ trigger, onSignIn }) {
  const { signedIn, patterns, signOut } = useLessonAccess();
  // Named by the private data (api/_lesson-data.js), so the name never ships in the bundle
  const unlockedName = patterns?.trayLabels?.major || 'Patterns';
  // Sign in opens its dialog only once the menu has closed and handed focus back to its
  // trigger; opening it straight from onSelect lets the menu pull focus off the password.
  const signInNext = useRef(false);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-64 rounded-xl border-line bg-tile text-ink p-1.5 animate-menu-in"
        onCloseAutoFocus={() => {
          if (!signInNext.current) return;
          signInNext.current = false;
          onSignIn();
        }}
      >
        <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
          <a href="/faq">
            <span className="flex h-[22px] w-[30px] items-center justify-center text-root-text">
              <InfoIcon size={20} />
            </span>
            <span className="flex flex-col">
              <span className="font-bold leading-tight">FAQ</span>
              <span className="text-[13px] leading-tight text-ink-muted">Common questions</span>
            </span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-1 bg-line" />
        <DropdownMenuItem asChild className={MENU_ITEM_CLASS}>
          <a href={TABBASE_URL} target="_blank" rel="noopener noreferrer">
            <TabBaseMark height={22} />
            <span className="flex flex-1 flex-col">
              <span className="font-bold leading-tight">TabBase</span>
              <span className="text-[13px] leading-tight text-ink-muted">Write guitar tab fast</span>
            </span>
            <ExternalIcon size={12} className="text-ink-muted" />
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="mx-1 bg-line" />
        <DropdownMenuItem
          onSelect={() => { if (signedIn) signOut(); else signInNext.current = true; }}
          className={MENU_ITEM_CLASS}
        >
          <span className="flex h-[22px] w-[30px] items-center justify-center text-root-text">
            <UserIcon size={20} />
          </span>
          {signedIn ? (
            <span className="flex flex-col">
              <span className="font-bold leading-tight">Sign out</span>
              <span className="text-[13px] leading-tight text-ink-muted">{unlockedName} are on</span>
            </span>
          ) : (
            <span className="font-bold leading-tight">Sign in</span>
          )}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const isPlainLeftClick = (e) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

export default function Header({ instrument, onInstrumentChange }) {
  const { theme, toggle } = useTheme();
  const isDark = theme === 'dark';
  const current = INSTRUMENTS.find((i) => i.key === instrument) ?? INSTRUMENTS[0];
  const [signInOpen, setSignInOpen] = useState(false);
  const openSignIn = () => setSignInOpen(true);

  const handleLinkClick = (e, key) => {
    // Let the browser handle modified clicks (new tab / window / download).
    if (e.defaultPrevented || !isPlainLeftClick(e)) return;
    e.preventDefault();
    onInstrumentChange(key);
  };

  return (
    <header
      className={cn(
        'w-full min-h-[52px] lg:min-h-[68px] bg-panel border-b border-line text-ink',
        'flex flex-wrap items-center justify-between gap-x-6 gap-y-2',
        'pl-4 pr-2 lg:px-5',
      )}
    >
      <Logo />

      {/* Desktop: instrument links */}
      <nav aria-label="Instrument" className="hidden lg:flex flex-wrap items-center">
        {INSTRUMENTS.map((inst, i) => {
          const active = inst.key === instrument;
          return (
            <React.Fragment key={inst.key}>
              {i > 0 && <span aria-hidden="true" className="w-px h-[18px] bg-line" />}
              <a
                href={`/${inst.slug}`}
                aria-current={active ? 'page' : undefined}
                onClick={(e) => handleLinkClick(e, inst.key)}
                className={cn(
                  'p-[14px] text-[15px] leading-5 transition-colors outline-none',
                  'focus-visible:ring-2 focus-visible:ring-root rounded-sm',
                  active
                    ? 'font-extrabold text-root-text'
                    : 'font-semibold text-ink-muted hover:text-ink',
                )}
              >
                {inst.label}
              </a>
            </React.Fragment>
          );
        })}
      </nav>

      {/* Desktop: info menu + dark-mode text button */}
      <div className="hidden lg:flex items-center gap-3">
        <InfoMenu
          onSignIn={openSignIn}
          trigger={
            <button
              type="button"
              aria-label={INFO_MENU_LABEL}
              className={cn(
                'group flex items-center gap-2 h-11 px-2.5 rounded-[6px]',
                'text-[15px] font-semibold text-root-text hover:bg-root/10 transition-colors',
                'data-[state=open]:bg-root/[0.12]',
                'outline-none focus-visible:ring-2 focus-visible:ring-root',
              )}
            >
              <InfoIcon size={16} />
              Info
              <ChevronDownIcon
                size={11}
                className="transition-transform duration-150 group-data-[state=open]:rotate-180"
              />
            </button>
          }
        />
        <button
          type="button"
          onClick={toggle}
          className={cn(
            'flex items-center gap-2 h-11 px-1.5 rounded-[6px]',
            'text-[15px] font-semibold text-root-text hover:bg-root/10 transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-root',
          )}
        >
          {isDark ? <SunIcon size={16} /> : <MoonIcon size={16} />}
          {isDark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {/* Phone: instrument chip + info button + icon-only dark-mode button */}
      <div className="flex lg:hidden items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* The button is the 44px hit area; the 36px chip look is the inner span. */}
            <button
              type="button"
              aria-label={`Instrument: ${current.label}`}
              className={cn(
                'group flex h-11 items-center rounded-[6px]',
                'outline-none focus-visible:ring-2 focus-visible:ring-root',
              )}
            >
              <span
                className={cn(
                  'flex h-9 items-center gap-1.5 rounded-[6px] bg-root/[0.12] px-3',
                  'text-[15px] font-bold text-root-text transition-colors group-hover:bg-root/20',
                  'group-data-[state=open]:bg-root/20',
                )}
              >
                {current.label}
                <ChevronDownIcon
                  size={12}
                  className="transition-transform duration-150 group-data-[state=open]:rotate-180"
                />
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-44 rounded-xl border-line bg-tile text-ink p-1.5 animate-menu-in"
          >
            {INSTRUMENTS.map((inst) => {
              const active = inst.key === instrument;
              return (
                <DropdownMenuItem
                  key={inst.key}
                  onSelect={() => onInstrumentChange(inst.key)}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'min-h-11 cursor-pointer rounded-lg px-3 py-2.5 text-[15px] justify-between',
                    'focus:bg-tray focus:text-ink',
                    active ? 'font-extrabold text-root-text focus:text-root-text' : 'font-semibold text-ink',
                  )}
                >
                  {inst.label}
                  {active && <CheckIcon size={16} />}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <InfoMenu
          onSignIn={openSignIn}
          trigger={
            <button
              type="button"
              aria-label={INFO_MENU_LABEL}
              className={cn(
                'w-11 h-11 flex items-center justify-center rounded-[6px]',
                'text-root-text hover:bg-root/10 transition-colors',
                'data-[state=open]:bg-root/[0.12]',
                'outline-none focus-visible:ring-2 focus-visible:ring-root',
              )}
            >
              <InfoIcon size={20} />
            </button>
          }
        />

        <button
          type="button"
          onClick={toggle}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          className={cn(
            'w-11 h-11 flex items-center justify-center rounded-[6px]',
            'text-root-text hover:bg-root/10 transition-colors',
            'outline-none focus-visible:ring-2 focus-visible:ring-root',
          )}
        >
          {isDark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
        </button>
      </div>

      <SignInDialog open={signInOpen} onClose={() => setSignInOpen(false)} />
    </header>
  );
}
